import React, { useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { useHistory } from 'react-router-dom';

import { teardownSession } from '../../../lib/services/auth.service';
import { useOfflineSyncSettled, useOnlineStatus } from '../../../lib/stores/connectivity.store';
import { useIntlSnackbar } from '../../../lib/hooks/use-intl-snackbar';
import { SessionType } from '../../../lib/types/session.types';
import { HOME_ROUTE } from '../../../../_configuration';
import { messages } from './idle-sign-out.messages';
import { useIdleTimer } from './idle-sign-out.use-idle-timer';
import { IdleWarningDialog } from './idle-warning-dialog.component';

interface IIdleSignOutMonitorProps {
  userId: string;
  timeoutMs: number;
}

// Signs out on this device only (no server logout, which ends every device). Waits until online and
// the offline replay has settled, so no queued action is sent without a token.
export const IdleSignOutMonitor: React.FC<IIdleSignOutMonitorProps> = ({ userId, timeoutMs }) => {
  const { warningRemainingMs, expired, keepAlive } = useIdleTimer({ userId, timeoutMs });
  const isOnline = useOnlineStatus();
  const syncSettled = useOfflineSyncSettled();
  const client = useApolloClient();
  const history = useHistory();
  const showSnackbar = useIntlSnackbar();
  const signingOutRef = useRef(false);

  useEffect(() => {
    if (!expired || !isOnline || !syncSettled || signingOutRef.current) return;
    signingOutRef.current = true;
    teardownSession(SessionType.STAFF, client)
      .then(() => {
        showSnackbar(messages.signedOut, 'info');
        history.push(HOME_ROUTE);
      })
      .catch((err) => {
        console.error('idle sign-out failed to tear down the session:', err);
        signingOutRef.current = false;
      });
  }, [expired, isOnline, syncSettled, client, history, showSnackbar]);

  if (warningRemainingMs === null) return null;
  return <IdleWarningDialog remainingMs={warningRemainingMs} onStaySignedIn={keepAlive} />;
};
